import React, { Suspense, lazy, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { useAuthStore, useCatStore } from './stores';
import { AppShell } from './components/layout';
import { LoginPage, SignupPage } from './components/auth';
import { LandingPage } from './components/landing';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { RouteErrorBoundary } from './components/shared/RouteErrorBoundary';
import { UnsavedChangesProvider } from './components/shared/UnsavedChangesGuard';
import { VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage } from './routes';
import { CarePassLayout } from './components/layout/CarePassLayout';

// Each signed-in and Care Pass screen is its own chunk, fetched when it is first
// opened, so a screen loads only what it uses — the charts (recharts) and the
// how-to guides' markdown renderer stay out of screens that do not show them.
// The service worker keeps every chunk for offline use (public/sw.js).
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const CatOnboarding = lazy(() => import('./components/cat-profile/CatOnboarding').then((m) => ({ default: m.CatOnboarding })));
const CatProfileView = lazy(() => import('./components/cat-profile/CatProfileView').then((m) => ({ default: m.CatProfileView })));
const CatProfileEdit = lazy(() => import('./components/cat-profile/CatProfileEdit').then((m) => ({ default: m.CatProfileEdit })));
const LabResultsPageWrapper = lazy(() => import('./routes/LabResultsPageWrapper').then((m) => ({ default: m.LabResultsPageWrapper })));
const VetPrepPageWrapper = lazy(() => import('./routes/VetPrepPageWrapper').then((m) => ({ default: m.VetPrepPageWrapper })));
const ExportPageWrapper = lazy(() => import('./routes/ExportPageWrapper').then((m) => ({ default: m.ExportPageWrapper })));
const MedicationsPageWrapper = lazy(() => import('./routes/MedicationsPageWrapper').then((m) => ({ default: m.MedicationsPageWrapper })));
const VetVisitsPageWrapper = lazy(() => import('./routes/VetVisitsPageWrapper').then((m) => ({ default: m.VetVisitsPageWrapper })));
const GuidancePageWrapper = lazy(() => import('./routes/GuidancePageWrapper').then((m) => ({ default: m.GuidancePageWrapper })));
const HowToLibraryPage = lazy(() => import('./routes/HowToLibraryPage').then((m) => ({ default: m.HowToLibraryPage })));
const HowToGuidePage = lazy(() => import('./routes/HowToGuidePage').then((m) => ({ default: m.HowToGuidePage })));
const WeightPage = lazy(() => import('./routes/WeightPage').then((m) => ({ default: m.WeightPage })));
const DailyLogPage = lazy(() => import('./routes/DailyLogPage').then((m) => ({ default: m.DailyLogPage })));
const SubqFluidsPage = lazy(() => import('./routes/SubqFluidsPage').then((m) => ({ default: m.SubqFluidsPage })));
const GlucosePage = lazy(() => import('./routes/GlucosePage').then((m) => ({ default: m.GlucosePage })));
const RestingRatePage = lazy(() => import('./routes/RestingRatePage').then((m) => ({ default: m.RestingRatePage })));
const QoLPage = lazy(() => import('./routes/QoLPage').then((m) => ({ default: m.QoLPage })));
const CaringForYouPage = lazy(() => import('./routes/CaringForYouPage').then((m) => ({ default: m.CaringForYouPage })));
const SettingsPage = lazy(() => import('./routes/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const CarePassLanding = lazy(() => import('./routes/care/CarePassLanding').then((m) => ({ default: m.CarePassLanding })));
const CarePassDailyLog = lazy(() => import('./routes/care/CarePassDailyLog').then((m) => ({ default: m.CarePassDailyLog })));
const CarePassWeight = lazy(() => import('./routes/care/CarePassWeight').then((m) => ({ default: m.CarePassWeight })));
const CarePassMedications = lazy(() => import('./routes/care/CarePassMedications').then((m) => ({ default: m.CarePassMedications })));
const CarePassFluids = lazy(() => import('./routes/care/CarePassFluids').then((m) => ({ default: m.CarePassFluids })));
const CarePassHowToLibrary = lazy(() => import('./routes/care/CarePassHowTo').then((m) => ({ default: m.CarePassHowToLibrary })));
const CarePassHowToGuide = lazy(() => import('./routes/care/CarePassHowTo').then((m) => ({ default: m.CarePassHowToGuide })));
const CarePassManagement = lazy(() => import('./routes/CarePassManagement').then((m) => ({ default: m.CarePassManagement })));
const CreateCarePass = lazy(() => import('./routes/CreateCarePass').then((m) => ({ default: m.CreateCarePass })));
const CarePassDetail = lazy(() => import('./routes/CarePassDetail').then((m) => ({ default: m.CarePassDetail })));
import './index.css';
import { DAILY_LOG, SUBQ_FLUIDS } from './lib/vocabulary';

// ============================================================================
// Route Wrappers
// ============================================================================

/** Full-page loading state shown while auth/cat state hydrates. Kept as one
 *  component so every guard renders identical loading UI. */
const HydrationFallback: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-sage-200 border-t-sage-600 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-gray-500">{t('app.loading')}</p>
      </div>
    </div>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);

  // Gate the redirect on auth hydration (Finding #5). loadStoredAuth() runs in
  // an effect AFTER the first render, so on a hard refresh / deep link to a
  // protected route, isAuthenticated is briefly false. Redirecting now would
  // send the user to /login — from which LoginPage forwards an authenticated
  // user to /dashboard — losing the route they actually requested. Waiting for
  // hydration keeps the URL intact so the requested route renders once known.
  if (!hasHydrated) {
    return <HydrationFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

interface OnboardingCheckProps {
  children: React.ReactNode;
}

export const OnboardingCheck: React.FC<OnboardingCheckProps> = ({ children }) => {
  const cats = useCatStore((state) => state.cats);
  const currentCat = useCatStore((state) => state.currentCat);
  const isLoading = useCatStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);

  // Don't redirect while cats are still being fetched — this prevents
  // a race condition where a page crash + error-boundary recovery triggers
  // fetchCats() again and the empty-array check fires before the fetch completes,
  // sending the user to onboarding even though they already have cats.
  if (isLoading) {
    return <HydrationFallback />;
  }

  // Phase 4: redirect to /onboarding whenever the user hasn't completed
  // the wizard. `onboarding_completed === false` is the authoritative
  // signal from the backend; legacy users (created before the flag) won't
  // have it set — for those we fall back to the cats-existence check so
  // we don't kick them out of the app on first deploy.
  const explicitlyIncomplete = user?.onboarding_completed === false;
  const legacyNoCats =
    user?.onboarding_completed === undefined && cats.length === 0 && !currentCat;

  if (explicitlyIncomplete || legacyNoCats) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// ============================================================================
// App Component
// ============================================================================

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { loadStoredAuth, isAuthenticated } = useAuthStore();
  const { fetchCats } = useCatStore();

  // Initialize auth and fetch cats on mount
  useEffect(() => {
    loadStoredAuth();
  }, [loadStoredAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCats().catch(() => {
        // Handle error silently
      });
    } else {
      // Not authenticated — no cats to fetch, clear loading state
      useCatStore.setState({ isLoading: false });
    }
  }, [isAuthenticated, fetchCats]);

  return (
    <BrowserRouter>
      {/* Keyed by language: switching language remounts every screen, so text a
          component resolved once (a memo, an initial state) is looked up again. */}
      <ErrorBoundary key={i18n.language}>
      <UnsavedChangesProvider>
      <AppShell>
        <Suspense fallback={<HydrationFallback />}>
        <Routes>
          {/* Root — landing page for visitors, dashboard for logged-in users */}
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LandingPage />
              )
            }
          />

          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Verify-email is public — the token in the URL is the proof. */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Care Pass Delegate Routes (standalone, no account required) */}
          <Route path="/care/:token" element={<CarePassLayout><CarePassLanding /></CarePassLayout>} />
          <Route path="/care/:token/daily-log" element={<CarePassLayout><CarePassDailyLog /></CarePassLayout>} />
          <Route path="/care/:token/weight" element={<CarePassLayout><CarePassWeight /></CarePassLayout>} />
          <Route path="/care/:token/fluids" element={<CarePassLayout><CarePassFluids /></CarePassLayout>} />
          <Route path="/care/:token/medications" element={<CarePassLayout><CarePassMedications /></CarePassLayout>} />
          {/* How-to guides are reference content, so delegates read them with no
              account and no pass scope — a sitter giving fluids needs guide 01. */}
          <Route path="/care/:token/how-to" element={<CarePassLayout><CarePassHowToLibrary /></CarePassLayout>} />
          <Route path="/care/:token/how-to/:slug" element={<CarePassLayout><CarePassHowToGuide /></CarePassLayout>} />

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <CatOnboarding />
              </ProtectedRoute>
            }
          />

          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.dashboard')}>
                    <DashboardPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />

          {/* Feature Routes — each wrapped in RouteErrorBoundary so a crash
              on one page keeps the sidebar, header, and active-cat state intact */}
          <Route
            path="/lab-results"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.labResults')}>
                    <LabResultsPageWrapper />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/daily-log"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={DAILY_LOG.title}>
                    <DailyLogPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/weight"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.weight')}>
                    <WeightPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subq-fluids"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={SUBQ_FLUIDS.label}>
                    <SubqFluidsPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/glucose"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.bloodGlucose')}>
                    <GlucosePage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/breathing-rate"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.breathingRate')}>
                    <RestingRatePage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/qol"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.qualityOfLife')}>
                    <QoLPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vet-prep"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.vetVisitPrep')}>
                    <VetPrepPageWrapper />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/export"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.exportData')}>
                    <ExportPageWrapper />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vet-visits"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.vetVisits')}>
                    <VetVisitsPageWrapper />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/medications"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.medications')}>
                    <MedicationsPageWrapper />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guidance"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.guidance')}>
                    <GuidancePageWrapper />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/how-to"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.howToGuides')}>
                    <HowToLibraryPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/how-to/:slug"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.howToGuide')}>
                    <HowToGuidePage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          {/* Caring for you — addressed to the OWNER, not the cat and not a
              delegate. ProtectedRoute is what keeps a Care Pass delegate out: a
              delegate has no account, so typing the URL sends them to /login. The
              nav entry is likewise never rendered for them, because AppShell drops
              the owner chrome on /care/:token. */}
          <Route
            path="/caring-for-you"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.caringForYou')}>
                    <CaringForYouPage />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <RouteErrorBoundary pageName={t('app.pageNames.settings')}>
                  <SettingsPage />
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Care Pass Management (Owner) */}
          <Route
            path="/care-passes"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.carePasses')}>
                    <CarePassManagement />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-passes/new"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.createCarePass')}>
                    <CreateCarePass />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-passes/:id"
            element={
              <ProtectedRoute>
                <OnboardingCheck>
                  <RouteErrorBoundary pageName={t('app.pageNames.carePassDetail')}>
                    <CarePassDetail />
                  </RouteErrorBoundary>
                </OnboardingCheck>
              </ProtectedRoute>
            }
          />

          {/* Cat Profile Routes */}
          <Route
            path="/cat-profile"
            element={
              <ProtectedRoute>
                <RouteErrorBoundary pageName={t('app.pageNames.catProfile')}>
                  <CatProfileView />
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cat-profile/edit"
            element={
              <ProtectedRoute>
                <RouteErrorBoundary pageName={t('app.pageNames.editCatProfile')}>
                  <CatProfileEdit />
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Catch All */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
      </AppShell>
      </UnsavedChangesProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default App;
